import CustomBreadcrumb from '@/components/custom-breadcrumb/custom-breadcrumb';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs/use-breadcrumbs';
import { Flex, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styles from './agents.module.scss';
import { useUser } from '@/hooks/use-user/use-user';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useAgents } from '@/hooks/use-agents/use-agents';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useOutlet } from 'react-router';
import { Button } from '@/components/button/button';
import { AgentStatusBadge } from '@/components/agent-status-badge/agent-status-badge';
import { ConversationalAgent } from '@/services/agents-service/agents.service.types';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

export function Agents() {
  const { t } = useTranslation('agents');
  const outlet = useOutlet();
  const navigate = useNavigate();
  const items = useBreadcrumbs();
  const { userData } = useUser();
  const { companies } = useCompaniesList();
  const isAdmin = userData?.is_admin || false;

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>();

  useEffect(() => {
    if (selectedCompanyId) return;
    if (userData?.company?.id) {
      setSelectedCompanyId(userData.company.id);
      return;
    }
    if (userData?.default_company && companies) {
      const matched = companies.find((c) => c.name === userData.default_company);
      if (matched) setSelectedCompanyId(matched.id);
    }
  }, [userData?.company?.id, userData?.default_company, companies, selectedCompanyId]);

  const { agents, isPending } = useAgents(selectedCompanyId);

  const selectedCompany = companies?.find((c) => c.id === selectedCompanyId);
  const activeAgentId = selectedCompany?.active_conversational_agent_id;

  const companyOptions =
    companies?.map((company) => ({
      label: company.name,
      value: company.id,
    })) || [];

  const columns: ColumnsType<ConversationalAgent> = [
    {
      title: t('labels.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <Flex align="center" gap={8}>
          <a onClick={() => navigate(`/agents/${record.id}`)}>{name}</a>
          {activeAgentId === record.id && <Tag color="blue">{t('list.tags.active')}</Tag>}
          {record.multilingual && <Tag color="green">{t('list.tags.multilingual')}</Tag>}
        </Flex>
      ),
    },
    {
      title: t('labels.status'),
      dataIndex: 'publish_status',
      key: 'publish_status',
      render: (status: string) => <AgentStatusBadge status={status} />,
    },
    {
      title: t('labels.phoneNumber'),
      key: 'phone_number',
      render: (_, record) =>
        record.twilio_phone_number?.friendly_name ||
        record.twilio_phone_number?.phone_number ||
        '\u2014',
    },
    {
      title: t('list.columns.knowledgeBase'),
      key: 'kb_count',
      render: (_, record) => record.conversational_agent_documents?.length || 0,
    },
    {
      title: t('list.columns.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" onClick={() => navigate(`/agents/${record.id}`)}>
          {t('list.view')}
        </Button>
      ),
    },
  ];

  return (
    <Flex vertical className={styles.container}>
      <CustomBreadcrumb items={items} />
      {outlet ? (
        <Outlet context={{ companyId: selectedCompanyId }} />
      ) : (
        <div>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              {/* One company is nothing to switch to. The effect above selects it anyway. */}
              {isAdmin && companyOptions.length !== 1 && (
                <Select
                  showSearch
                  placeholder={t('list.selectCompany')}
                  value={selectedCompanyId}
                  onChange={setSelectedCompanyId}
                  options={companyOptions}
                  filterOption={(input, option) =>
                    (option?.label as string).toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: 240 }}
                />
              )}
            </div>
            <Button buttonVariant="primary" onClick={() => navigate('/agents/new')}>
              {t('createAgent')}
            </Button>
          </div>

          <Table
            className={styles.table}
            columns={columns}
            dataSource={agents}
            rowKey="id"
            loading={isPending}
            pagination={false}
          />
        </div>
      )}
    </Flex>
  );
}
